import { Box, Stack, styled } from '@mui/material';
import { useMemo } from 'react';
import { CopyToClipboardButton, DynamicForm, FormAutocompleteProps } from 'src/components';
import { CONTAINER_CLASS, LabelField } from 'src/components/Fields/LabelField';
import { SiteAddress } from 'src/components/SiteComponents/site-address';
import {
  Address,
  onlyPublicLabelsFilter,
  useEntityAssignments,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import {
  SITE_CARD_PROPERTY_KEYS,
  useSectionVisibility,
} from 'src/graphql/entities/side-panel-personalization';
import { ConditionInput } from 'src/graphql/types';
import { useOpenSite } from 'src/utils/hooks';

export const StyledWrapper = styled(Stack, {
  name: 'SiteTabWrapper',
})(({ theme }) => ({
  borderTop: '1px solid',
  borderColor: theme.palette.custom.OV.sys.outlineVariant,
  marginTop: theme.spacing(2),
  flexGrow: 1,
}));

export const SideTabStyledWrapper = styled(StyledWrapper)(() => ({
  borderTop: 0,
  marginTop: 0,
}));

const NO_FIELDS: FormAutocompleteProps[] = [];

export const SiteTab = <
  T extends {
    serviceLevel: { id: string; label: string };
    accountManager: { id: string; label: string };
  },
>({
  title,
  addressRightAdornment,
  children,
  address,
  onSubmit,
  fields = NO_FIELDS,
  disabled = false,
  propertiesAsRows = false,
  hideAddressRow = false,
  propertiesHeader,
}: {
  title?: React.ReactNode;
  addressRightAdornment?: React.ReactNode;
  children?: React.ReactNode;
  address: Address;
  fields?: FormAutocompleteProps[];
  onSubmit?: (values: T) => void;
  disabled?: boolean;
  /**
   * The Site page renders the base fields as ordered personalization rows
   * (site-card-rows.tsx) inside `children` — skip the card's own form + labels.
   */
  propertiesAsRows?: boolean;
  /** Omit the address row when a host (e.g. a SidePanelCard header) already shows the address as its title. */
  hideAddressRow?: boolean;
  /** Rendered above the form fields — the card's actions row and its "Properties" caption. */
  propertiesHeader?: React.ReactNode;
}) => {
  const { groupRules } = useGroupRules();

  // labels are fetched here, so only this card can feed the hide-empty half of
  // the gate; the toggle half also skips the fetch entirely
  const isVisible = useSectionVisibility('site');
  const showLabels = !propertiesAsRows && isVisible(SITE_CARD_PROPERTY_KEYS.labels);

  const openSite = useOpenSite();

  const canAccessToPrivateLabels = groupRules[UserGroupRules.AccessToPrivateLabels];

  const labelsFilters: ConditionInput[] = useMemo(
    () => (canAccessToPrivateLabels ? [] : [onlyPublicLabelsFilter]),
    [canAccessToPrivateLabels],
  );

  const { labelAssignments } = useEntityAssignments({
    entity: 'Address',
    ids: address?.addressId,
    filters: labelsFilters,
    skip: !groupRules || !showLabels,
  });

  return (
    <SideTabStyledWrapper minHeight={0}>
      {title}
      {!hideAddressRow ? (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center" overflow="hidden">
            <SiteAddress
              address={address}
              onClick={(event: React.MouseEvent<HTMLElement>) => openSite(event, address)}
              tooltipText="Click to open site"
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <CopyToClipboardButton content={address.fullAddress!} />
            {addressRightAdornment}
          </Stack>
        </Stack>
      ) : null}
      <Stack spacing={2} flexGrow={1} minHeight={0}>
        {propertiesAsRows ? (
          propertiesHeader
        ) : (
          <Stack>
            {propertiesHeader}
            <DynamicForm
              watchable
              watchableSubmitOnlyChangedField
              fields={fields}
              onSubmit={onSubmit!}
            />
          </Stack>
        )}
        {showLabels && isVisible(SITE_CARD_PROPERTY_KEYS.labels, !labelAssignments?.length) ? (
          <Box
            className={CONTAINER_CLASS}
            sx={{
              marginTop: (theme) => theme.utils.important(0),
            }}
          >
            <LabelField
              entityId={address?.addressId}
              entityName="Address"
              partnerId={address?.partnerId}
              labelAssignments={labelAssignments}
              isEditable={!disabled}
              paddingInline={2}
              paddingTop={1}
              canAccessToPrivateLabels={canAccessToPrivateLabels as boolean}
              sx={{
                marginTop: (theme) => theme.utils.important(0),
              }}
            />
          </Box>
        ) : null}
        {children}
      </Stack>
    </SideTabStyledWrapper>
  );
};
